import { useState, useRef, useEffect } from 'react';
import i18n from "../../../../utils/translations/i18n";
import ErrorMsg from '../errorMsg';
import Styles from './index.module.scss';

const OtpInput = ({ length, onChange, onComplete, errorMessage, reset }) => {
  const { t } = i18n;
  const [otp, setOtp] = useState(Array(length).fill(''));
  const inputs = useRef<HTMLInputElement[]>([]);

  useEffect(() => {
    setOtp(Array(length).fill(''));
    onChange('');
    inputs?.current[0]?.focus();
  }, [reset])

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    const key = e.key.toLowerCase();
    const newOtp = [...otp];
    switch (key) {
      case 'backspace':
      case 'delete': {
        if (otp[index]) {
          newOtp[index] = '';
          setOtp(newOtp)
        } else if (index > 0) {
          inputs.current[index - 1].focus();
          newOtp[index - 1] = '';
          setOtp(newOtp)
        }
        break;
      }
      case 'arrowleft': {
        if (index > 0) inputs.current[index - 1].focus();
        break;
      }
      case 'arrowright': {
        if (index < length - 1) inputs.current[index + 1].focus();
        break;
      }
    }
    onChange(newOtp.join(''));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> , index: number) => {
    e.preventDefault();
    const value = e.target.value;
    const newOtp = [...otp];
    if(/^[0-9]+$/.test(value)){
      if (value.length === 1 ) {
        newOtp[index] = value;
        setOtp(newOtp);
        
        const compeleteOTP = newOtp.join('');
        
        if (index < length - 1) {
          inputs.current[index + 1].focus();
        } else if (compeleteOTP.length !== length) {
          inputs.current[otp.findIndex(digit => digit === '')].focus();
        } else {
          inputs.current[index].blur();
          onComplete();
        }
      }else{
        pasteDataHandler(value,index);
        onComplete();
        return;
      }
    }
    onChange(newOtp.join(''));
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
    if(paste){
      pasteDataHandler(paste,index);
    }
  };

  const pasteDataHandler = (val: string, index: number) => {
    const newOtp = [...otp];
  
    // Only paste as many characters as there are remaining input fields
    for (let i = 0; i < length - index && i < val.length; i++) {
      newOtp[index + i] = val[i];
    }

    setOtp(newOtp);
    const compeleteOTP = newOtp.join('');
    onChange(newOtp.join(''));

    // Focus on the next unfilled input
    const lastFilledIndex = Math.min(index + val.length - 1, length - 1);
    
    if (lastFilledIndex < length - 1) {
      inputs.current[lastFilledIndex + 1].focus();
    } else if (compeleteOTP.length !== length) {
      inputs.current[otp.findIndex(digit => digit === '')].focus();
    } else {
      inputs.current[index].blur();
      onComplete();
    }
  }

  return (
    <div>
        <div className={Styles.container}>
          {otp.map((_, index) => (
            <input
            key={''+index}
            type="text"
            inputMode="numeric"
            value={otp[index]}
            className={errorMessage && errorMessage.length>0 ? Styles.dangerWarn : ''}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyUp(e, index)}
            onPaste={(e) => handlePaste(e, index)}
            ref={(ref) => (inputs.current[index] = ref as HTMLInputElement)}
            autoFocus={index === 0}
            aria-label={`${t('email_otp.otp_digit_aria_label')}`}
            />
            ))}
      </div>
      <div className={Styles.errorContainer}>
          {errorMessage && errorMessage.length>0 && <ErrorMsg fieldName={'otp'} error={errorMessage}/>}
      </div>
      </div>
  );
};

export default OtpInput;
